/**
 * The unit last chosen for each food's amount (cups for flour, tbsp for butter), remembered on
 * this device by the food's ref. Only refs and unit names are stored, for the most recent foods,
 * and they're forgotten when someone signs in or out, so another account on the same device
 * neither inherits them nor sees which foods they name. Without storage (blocked, private
 * browsing, tests) nothing is remembered and every amount starts in grams.
 */
import { isKitchenUnit, type AmountUnit, type KitchenUnit } from '../../shared/kitchenMeasures';

export const AMOUNT_UNITS_KEY = 'grams.amountUnits';
/** Foods remembered. Past this many, the one chosen longest ago is forgotten. */
export const REMEMBERED_FOODS = 200;

/** The part of Web Storage used here, so tests can pass their own. */
export interface UnitStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function deviceStore(): UnitStore | undefined {
  try {
    return (globalThis as { localStorage?: UnitStore }).localStorage;
  } catch {
    // Reading localStorage itself throws where storage is blocked.
    return undefined;
  }
}

/** Each food and its unit, most recent first, from a stored value. Anything else in it is ignored. */
export function parseAmountUnits(raw: string | null): Array<[string, KitchenUnit]> {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter((pair): pair is [string, KitchenUnit] => Array.isArray(pair) && typeof pair[0] === 'string' && pair[0] !== '' && isKitchenUnit(pair[1]));
  } catch {
    return [];
  }
}

function read(store: UnitStore | undefined): Array<[string, KitchenUnit]> {
  try {
    return parseAmountUnits(store?.getItem(AMOUNT_UNITS_KEY) ?? null);
  } catch {
    return [];
  }
}

/** The unit last chosen for a food, or null for grams (nothing chosen, or nothing remembered). */
export function readAmountUnit(ref: string, store: UnitStore | undefined = deviceStore()): KitchenUnit | null {
  return read(store).find(([r]) => r === ref)?.[1] ?? null;
}

/** Forgets every food's unit: at sign-in, sign-out and deleting an account. */
export function forgetAmountUnits(store: UnitStore | undefined = deviceStore()): void {
  try {
    store?.removeItem(AMOUNT_UNITS_KEY);
  } catch {
    // Blocked storage: there was nothing it could have kept.
  }
}

/** Remembers a food's unit. Grams, the default, is remembered by forgetting the food. */
export function writeAmountUnit(ref: string, unit: AmountUnit, store: UnitStore | undefined = deviceStore()): void {
  const others = read(store).filter(([r]) => r !== ref);
  const next = unit === 'g' ? others : [[ref, unit] as [string, KitchenUnit], ...others].slice(0, REMEMBERED_FOODS);
  try {
    if (next.length > 0) store?.setItem(AMOUNT_UNITS_KEY, JSON.stringify(next));
    else store?.removeItem(AMOUNT_UNITS_KEY);
  } catch {
    // Full or blocked storage: the unit stays for this visit, just not remembered.
  }
}
